import { Eye, MonitorPlay, Video } from 'lucide-react'
import { MriButton } from '@mriqbox/ui-kit'
import { Avatar } from '@/components/Avatar'
import type { StageStream } from '@/components/StreamTile'

export interface AvailableStreamsProps {
    streams: StageStream[]
    onWatch: (id: string) => void
    onWatchAll: () => void
}

function describe(entry: StageStream): string {
    if (entry.isSelf) return entry.kind === 'camera' ? 'Sua câmera, prévia oculta' : 'Sua tela, prévia oculta'
    return entry.kind === 'camera' ? 'está com a câmera ligada' : 'está compartilhando a tela'
}

/** Shown when nothing is on stage yet: nothing is received until someone clicks. */
export function AvailableCards({ streams, onWatch, onWatchAll }: AvailableStreamsProps) {
    return (
        <div className="flex w-full max-w-2xl flex-col items-center gap-3">
            <p className="text-sm text-muted-foreground">
                Nada é baixado até você escolher o que assistir.
            </p>
            <ul className="flex w-full flex-wrap justify-center gap-2">
                {streams.map((entry) => (
                    <li
                        key={entry.id}
                        className="flex w-full min-w-0 items-center gap-3 rounded-xl border border-border/70 bg-card/70 p-3 sm:w-[20rem]"
                    >
                        <Avatar nickname={entry.nickname} avatar={entry.avatar} size={40} />
                        <div className="min-w-0 flex-1 text-left">
                            <p className="truncate text-sm font-semibold text-foreground">
                                {entry.isSelf ? 'Você' : entry.nickname}
                            </p>
                            <p className="flex items-center gap-1 text-xs text-muted-foreground">
                                {entry.kind === 'camera'
                                    ? <Video className="h-3 w-3 shrink-0" aria-hidden />
                                    : <MonitorPlay className="h-3 w-3 shrink-0" aria-hidden />}
                                <span className="truncate">{describe(entry)}</span>
                            </p>
                        </div>
                        <MriButton size="sm" onClick={() => onWatch(entry.id)}>
                            {entry.isSelf ? 'Mostrar' : 'Assistir'}
                        </MriButton>
                    </li>
                ))}
            </ul>
            {streams.filter((entry) => !entry.isSelf).length > 1 && (
                <MriButton variant="secondary" size="sm" onClick={onWatchAll}>
                    Assistir todas
                </MriButton>
            )}
        </div>
    )
}

/** With something already on stage, the rest stays one click away in a thin strip. */
export function AvailableStrip({ streams, onWatch, onWatchAll }: AvailableStreamsProps) {
    if (streams.length === 0) return null
    return (
        <div className="mb-2 flex shrink-0 flex-wrap items-center gap-1.5">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
                Também no ar
            </span>
            {streams.map((entry) => (
                <button
                    key={entry.id}
                    type="button"
                    onClick={() => onWatch(entry.id)}
                    title={entry.isSelf ? 'Mostrar a sua prévia' : `Assistir ${entry.nickname}`}
                    className="flex items-center gap-1.5 rounded-full border border-border/70 bg-card/70 px-2.5 py-1 text-xs text-muted-foreground transition-colors hover:border-primary/50 hover:text-foreground"
                >
                    <Eye className="h-3 w-3" aria-hidden />
                    <span className="max-w-28 truncate">{entry.isSelf ? 'Você' : entry.nickname}</span>
                    <span className="text-[10px] opacity-70">
                        {entry.kind === 'camera' ? 'câmera' : 'tela'}
                    </span>
                </button>
            ))}
            {streams.filter((entry) => !entry.isSelf).length > 1 && (
                <button
                    type="button"
                    onClick={onWatchAll}
                    className="rounded-full px-2 py-1 text-xs font-medium text-primary hover:underline"
                >
                    Assistir todas
                </button>
            )}
        </div>
    )
}
